import { useWs, type WsStatus } from "@/providers/ws-provider";
import { useEventStreamStatus } from "@/hooks/use-event-stream";
import type { StreamStatus } from "@/lib/event-stream";

type CombinedStatus = "connected" | "connecting" | "disconnected";

const DOT_CLASS: Record<CombinedStatus, string> = {
  connected: "bg-success",
  connecting: "bg-warn animate-pulse",
  disconnected: "bg-danger",
};

// Severity ladder — higher = worse. Used to compute the "min" (worst) status
// between the two transports, so the pill always reflects the weakest link.
const SEVERITY: Record<CombinedStatus, number> = {
  connected: 0,
  connecting: 1,
  disconnected: 2,
};

function normalizeStream(s: StreamStatus): CombinedStatus {
  return s === "closed" ? "disconnected" : s;
}

function normalizeWs(s: WsStatus): CombinedStatus {
  return s;
}

function worstOf(a: CombinedStatus, b: CombinedStatus): CombinedStatus {
  return SEVERITY[a] >= SEVERITY[b] ? a : b;
}

const LABEL: Record<CombinedStatus, string> = {
  connected: "connected",
  connecting: "connecting…",
  disconnected: "disconnected",
};

/**
 * WS connection indicator as a non-interactive pill "WS •" (dot color
 * reflects the worst of OKX WebSocket + bot SSE events stream).
 *
 * Rationale for min()-combining: both transports are required for a "live"
 * UI — OKX WS drives live prices, SSE drives DB change invalidation. If
 * either drops, the user's view is degraded, so the pill must surface that
 * immediately. Tooltip breaks out per-transport state for diagnosis.
 */
export default function WsStatusDot() {
  const { status: wsStatus } = useWs();
  const sseStatus = useEventStreamStatus();

  const wsNorm = normalizeWs(wsStatus);
  const sseNorm = normalizeStream(sseStatus);
  const combined = worstOf(wsNorm, sseNorm);

  const tooltip = `OKX WS: ${LABEL[wsNorm]}\nEvents: ${LABEL[sseNorm]}`;

  return (
    <span
      className="inline-flex items-center gap-1.5 h-8 rounded px-2.5 text-xs font-medium leading-none border border-border text-muted cursor-default select-none"
      title={tooltip}
      aria-label={tooltip}
    >
      WS
      <span
        className={[
          "inline-block w-2 h-2 rounded-full flex-shrink-0",
          DOT_CLASS[combined],
        ].join(" ")}
      />
    </span>
  );
}
